import React, { useEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { isNativeApp } from '../utils/platform';
import { readAppStoreConfig } from '../shared/appStore';
import {
  INVITE_INVALID_MESSAGE,
  START_OWN_ACCOUNT_LABEL,
  choiceAllowed,
} from '../shared/friendInvite';
import { ASK_YOUR_SPONSOR_TEXT, nativeOwnAccountAllowed } from '../shared/nativeSignupCopy';
import { signupFieldErrors, SIGNUP_DUPLICATE_MESSAGE } from '../shared/homeownerSignup';
import { HOMEOWNER_BUTTON } from '../shared/consentCopy';
import { loginHomeowner, requestFriendSponsor, resolveFriendLink, submitFriendJoin } from '../utils/homeownerApi';
import { CountyField } from './ServiceAreaFields';
import FriendStatus from './FriendStatus';

function storeOffer() {
  const fromWindow = typeof window !== 'undefined' ? window.PACARION_APP_STORE : null;
  const source = fromWindow || {};
  return readAppStoreConfig({
    id: source.id || process.env.REACT_APP_APP_STORE_ID || '',
    url: source.url || process.env.REACT_APP_APP_STORE_URL || '',
  });
}

function inputStyle(colors) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: '0.75rem',
    padding: '0.85rem',
    borderRadius: '8px',
    border: `2px solid ${colors.borderLight || colors.border}`,
    background: colors.surface,
    color: colors.text,
    fontSize: '1rem',
    minHeight: '48px',
  };
}

function pageStyle(colors) {
  return {
    background: colors.background,
    color: colors.text,
    minHeight: '100vh',
    boxSizing: 'border-box',
    paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))',
    paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
    paddingLeft: 'calc(1.25rem + env(safe-area-inset-left, 0px))',
    paddingRight: 'calc(1.25rem + env(safe-area-inset-right, 0px))',
  };
}

const primaryButton = {
  width: '100%',
  minHeight: '48px',
  border: 'none',
  borderRadius: '12px',
  background: '#59c4c4',
  color: '#083838',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  marginBottom: '0.75rem',
};

export default function FriendJoin({
  token,
  native,
  signedIn,
  initialView,
  initialLink,
  resolveLink,
  submitJoin,
  requestSponsor,
  signIn,
  onSignedUp,
  onStartOwn,
  onSignIn,
}) {
  const { colors, isDark } = useTheme();
  const onDevice = native === undefined ? isNativeApp() : native === true;
  const allowOwn = nativeOwnAccountAllowed(onDevice);
  const [view, setView] = useState(initialView || (initialLink ? 'choices' : 'loading'));
  const [link, setLink] = useState(initialLink || null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [countyFips, setCountyFips] = useState('');
  const [countySource, setCountySource] = useState('');
  const [accepted, setAccepted] = useState(false);
  const store = storeOffer();

  useEffect(() => {
    if (initialLink || initialView === 'pending' || initialView === 'declined') return undefined;
    if (!token) {
      setView('invalid');
      return undefined;
    }
    let cancel = false;
    const load = resolveLink || resolveFriendLink;
    load(token).then((data) => {
      if (cancel) return;
      setLink(data);
      setView('choices');
    }).catch((loadError) => {
      if (cancel) return;
      setError((loadError && loadError.message) || INVITE_INVALID_MESSAGE);
      setView('invalid');
    });
    return () => { cancel = true; };
  }, [token, initialLink, initialView, resolveLink]);

  const choice = view === 'sponsor' || view === 'own' ? view : '';
  const showSponsor = link && choiceAllowed(link, 'sponsor');
  const agentName = (link && link.agentFirstName) || 'your sponsor';
  const inviterName = (link && link.inviterFirstName) || 'A homeowner';

  const finish = (result) => {
    if (result && result.joinRequest && result.joinRequest.status === 'pending') {
      setLink((current) => current || {
        agentFirstName: result.joinRequest.agentFirstName,
      });
      if (result.joinRequest.agentFirstName) {
        setLink((current) => ({ ...(current || {}), agentFirstName: result.joinRequest.agentFirstName }));
      }
      setView('pending');
    }
    if (onSignedUp) onSignedUp(result);
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      ref: token,
      choice,
      name,
      email,
      password,
      phone,
      address,
      city,
      state,
      zip,
      countyFips,
      countySource,
      accepted,
    };
    const errors = signupFieldErrors(payload);
    if (errors.length) {
      setError(errors[0]);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const send = submitJoin || submitFriendJoin;
      const result = await send(payload);
      finish(result);
    } catch (submitError) {
      const message = (submitError && submitError.message) || 'Could not create the account.';
      setError(message);
      if (message === SIGNUP_DUPLICATE_MESSAGE) setView('signin');
    } finally {
      setSaving(false);
    }
  };

  const signInAndRequest = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const enter = signIn || loginHomeowner;
      await enter({ email, password });
      const ask = requestSponsor || requestFriendSponsor;
      const result = await ask({ ref: token, phone });
      finish(result);
      setView('pending');
    } catch (signInError) {
      setError((signInError && signInError.message) || 'Could not sign in.');
    } finally {
      setSaving(false);
    }
  };

  const askWhileSignedIn = async () => {
    setSaving(true);
    setError('');
    try {
      const ask = requestSponsor || requestFriendSponsor;
      const result = await ask({ ref: token, phone });
      finish(result);
      setView('pending');
    } catch (askError) {
      setError((askError && askError.message) || 'Could not send the request.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="friend-join" style={pageStyle(colors)}>
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>
        {view === 'loading' ? <p>Loading...</p> : null}
        {view === 'invalid' ? <p role="alert">{error || INVITE_INVALID_MESSAGE}</p> : null}
        {view === 'choices' && link ? (
          <div>
            <h1 style={{ fontSize: '1.7rem', fontWeight: 650, margin: '0 0 0.5rem' }}>{inviterName} invited you</h1>
            <p
              data-testid={!allowOwn && !showSponsor ? 'native-ask-sponsor' : undefined}
              style={{
                color: !allowOwn && !showSponsor ? colors.text : colors.textSecondary,
                fontWeight: !allowOwn && !showSponsor ? 650 : 400,
                marginTop: 0,
                lineHeight: 1.5,
              }}
            >
              {allowOwn
                ? (showSponsor
                  ? `Ask ${agentName} to add you, or start your own Pacarion account.`
                  : 'You can start your own Pacarion account.')
                : (showSponsor
                  ? `Ask ${agentName} to add you.`
                  : ASK_YOUR_SPONSOR_TEXT)}
            </p>
            {showSponsor ? (
              <button type="button" data-testid="friend-join-sponsor" onClick={() => signedIn ? askWhileSignedIn() : setView('sponsor')} style={primaryButton}>
                {`Ask ${agentName} to add you`}
              </button>
            ) : null}
            {allowOwn ? (
              <button type="button" data-testid="friend-join-own" onClick={() => setView('own')} style={showSponsor ? { ...primaryButton, background: 'transparent', color: colors.text, border: `1px solid ${colors.border}` } : primaryButton}>
                {START_OWN_ACCOUNT_LABEL}
              </button>
            ) : showSponsor ? (
              <p data-testid="native-ask-sponsor" style={{ margin: '0.25rem 0 0', fontSize: '1.05rem', lineHeight: 1.5, fontWeight: 650 }}>
                {ASK_YOUR_SPONSOR_TEXT}
              </p>
            ) : null}
            {!onDevice && store.url ? (
              <a href={store.url} style={{ display: 'inline-flex', alignItems: 'center', minHeight: '44px', color: isDark ? '#5eead4' : '#0f766e', fontWeight: 700 }}>
                Download on the App Store
              </a>
            ) : null}
            {error ? <p role="alert">{error}</p> : null}
          </div>
        ) : null}
        {view === 'own' && !allowOwn ? (
          <p data-testid="native-ask-sponsor" style={{ margin: 0, fontSize: '1.05rem', lineHeight: 1.5, fontWeight: 650 }}>
            {ASK_YOUR_SPONSOR_TEXT}
          </p>
        ) : null}
        {((view === 'sponsor') || (view === 'own' && allowOwn)) ? (
          <form onSubmit={submit}>
            <button type="button" onClick={() => setView('choices')} style={{ background: 'transparent', border: 'none', color: colors.primary || '#0f766e', fontWeight: 650, minHeight: '44px', padding: 0, cursor: 'pointer' }}>
              Back
            </button>
            <h1 style={{ fontSize: '1.5rem', margin: '0.25rem 0 0.75rem' }}>
              {view === 'sponsor' ? `Ask ${agentName}` : 'Create your account'}
            </h1>
            <input aria-label="Name" placeholder="Name" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} style={inputStyle(colors)} />
            <input aria-label="Email" type="email" placeholder="Email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} style={inputStyle(colors)} />
            <input aria-label="Password" type="password" placeholder="Password (min 8 characters)" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} style={inputStyle(colors)} />
            <input aria-label="Phone" placeholder="Phone (optional)" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value)} style={inputStyle(colors)} />
            <input aria-label="Home address" placeholder="Home address" autoComplete="street-address" value={address} onChange={(event) => setAddress(event.target.value)} style={inputStyle(colors)} />
            <input aria-label="City" placeholder="City" value={city} onChange={(event) => setCity(event.target.value)} style={inputStyle(colors)} />
            <input aria-label="State" placeholder="State" value={state} onChange={(event) => setState(event.target.value)} style={inputStyle(colors)} />
            <input aria-label="ZIP code" placeholder="ZIP code" inputMode="numeric" value={zip} onChange={(event) => setZip(event.target.value)} style={inputStyle(colors)} />
            <CountyField
              zip={zip}
              state={state}
              fips={countyFips}
              source={countySource}
              colors={colors}
              onChange={(county) => {
                setCountyFips((county && county.property_county_fips) || '');
                setCountySource((county && county.property_county_source) || '');
              }}
            />
            <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', margin: '0.25rem 0 1rem' }}>
              <input
                type="checkbox"
                checked={accepted}
                aria-label="Agree to the Terms of Service and Privacy Policy"
                onChange={(event) => setAccepted(event.target.checked)}
                style={{ marginTop: '0.2rem', width: '1.1rem', height: '1.1rem', flexShrink: 0 }}
              />
              <span>
                I am 18 or older and agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#0f766e', fontWeight: 650 }}>Terms of Service</a>
                {' '}
                (including the <strong>arbitration agreement and class action waiver</strong>, which I may opt out of within 30 days), and I have read the{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#0f766e', fontWeight: 650 }}>Privacy Policy</a>.
              </span>
            </label>
            {error ? <p role="alert" style={{ color: isDark ? '#fecaca' : '#991b1b' }}>{error}</p> : null}
            <button type="submit" disabled={saving || !accepted} style={{ ...primaryButton, opacity: saving || !accepted ? 0.6 : 1 }}>
              {saving ? 'Sending...' : HOMEOWNER_BUTTON}
            </button>
          </form>
        ) : null}
        {view === 'signin' ? (
          <form onSubmit={signInAndRequest}>
            <h1 style={{ fontSize: '1.5rem' }}>Sign in</h1>
            <p style={{ color: colors.textSecondary }}>{SIGNUP_DUPLICATE_MESSAGE}</p>
            <input aria-label="Email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} style={inputStyle(colors)} />
            <input aria-label="Password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} style={inputStyle(colors)} />
            {error ? <p role="alert">{error}</p> : null}
            <button type="submit" disabled={saving} style={primaryButton}>Sign in and ask</button>
            <button type="button" onClick={onSignIn} style={quietLinkStyle()}>Use the main sign-in screen</button>
          </form>
        ) : null}
        {view === 'pending' || view === 'declined' ? (
          <FriendStatus
            status={view}
            agentFirstName={link && link.agentFirstName}
            native={onDevice}
            onStartOwn={onStartOwn}
          />
        ) : null}
      </div>
    </div>
  );
}

function quietLinkStyle() {
  return {
    background: 'none',
    border: 'none',
    color: '#0f766e',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '44px',
    fontSize: '1rem',
  };
}
